import React, { useState } from "react"
import { api, ApiError } from "../../lib/api"
import { formatCorte } from "../../lib/narrativa"
import type { Cortes } from "../../types"

interface Props {
  cortes: Cortes
  /** Lista já escolhida (users.lista_esa) ou nula. */
  lista: string | null
  onChange: (lista: string) => void
  /** Variante escura (dentro do hero navy). */
  dark?: boolean
}

/**
 * Um toque: "Qual é a sua lista?" O corte da ESA é por lista (masculina ou
 * feminina × ampla concorrência ou cotas) e o app não tem como inferir — só
 * perguntar. Enquanto a pessoa não escolhe, nenhuma tela decide "passou".
 * Grava em PATCH /api/me e avisa o pai para recalcular o veredito na hora.
 */
export function ListaSelector({ cortes, lista, onChange, dark = false }: Props) {
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function choose(key: string) {
    if (key === lista || saving) return
    setSaving(key)
    setError(null)
    try {
      await api.patch("/api/me", { user: { lista_esa: key } })
      onChange(key)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Não deu pra salvar. Tente de novo.")
    } finally {
      setSaving(null)
    }
  }

  const base = dark
    ? "border-white/15 bg-white/5 text-navy-100 hover:border-gold-400/60"
    : "border-slate-200 bg-white text-navy-800 hover:border-navy-300"
  const active = dark ? "border-gold-400 bg-gold-400/15 text-white" : "border-gold-400 bg-gold-50 text-navy-900 ring-1 ring-gold-400"

  return (
    <div className="mt-4">
      <p className={`text-xs font-semibold uppercase tracking-wider ${dark ? "text-gold-400" : "text-navy-500"}`}>
        {lista ? "Sua lista na ESA" : "Qual é a sua lista na ESA?"}
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {cortes.listas.map((l) => (
          <button
            key={l.key}
            type="button"
            onClick={() => void choose(l.key)}
            aria-pressed={lista === l.key}
            disabled={saving !== null}
            className={`rounded-xl border px-3 py-2 text-left text-xs transition-colors disabled:opacity-60 ${lista === l.key ? active : base}`}
          >
            <span className="block font-semibold capitalize">{l.rotulo}</span>
            <span className={dark ? "text-navy-300" : "text-navy-500"}>corte {cortes.ano}: {formatCorte(l.nota)}</span>
          </button>
        ))}
      </div>
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
    </div>
  )
}
